import { Injectable, NgZone } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { createElement } from 'src/app/core/helpers/dom';
import { WorkspaceService } from '../../workspace/workspace.service';
import { TExportDashlet } from '../dashboard-export-modal/dashboard-export-dashlet.model';
import { DashboardReportService } from '../dashboard-report.service';
import { TChartSettings } from '../dashboard.model';
import { TDashletGraph } from '../dashlet-graph.model';
import { CreateCostComparisonGraphService } from '../dashlet/dashlet-service/create-cost-comparison-graph.service';
import { CreateStackedStatusGraphService } from '../dashlet/dashlet-service/create-stacked-status-graph.service';
import { CreateValueBreakdownGraphService } from '../dashlet/dashlet-service/create-value-breakdown-graph.service';
import { DashletService } from '../dashlet/dashlet-service/dashlet.service';
import { EDashletType } from '../dashlets-enums';
import { createDashletHeaderElement } from './create-dashlet-head-element';
import { createTotalPointsElement } from './create-total-points-element';
import {
  generateImage,
  TDashboardImageElementData,
} from './dashboard-export-elements/dashboard-image-element';
import {
  advancedDashletBorderRadius,
  advancedDashletCanvasWidth,
  advancedDashletGraphHeight,
  advancedDashletHeight,
  advancedDashletImageWidth,
} from './dashboard-export-variables';

type TDashletGraphSettings = {
  dashlet: TExportDashlet;
  canvas: HTMLCanvasElement;
  imageWrapper: HTMLElement;
  image: HTMLImageElement;
  scale: number;
  assetId?: string;
};

@Injectable({
  providedIn: 'root',
})
export class DashboardAdvancedDashletService {
  constructor(
    private dashletService: DashletService,
    private dashboardReportService: DashboardReportService,
    private ngZone: NgZone,
    private createCostComparisonGraphService: CreateCostComparisonGraphService,
    private createStackedStatusGraphService: CreateStackedStatusGraphService,
    private createValueBreakdownGraphService: CreateValueBreakdownGraphService,
    private workspaceService: WorkspaceService,
  ) {}

  generateAdvancedDashlet(
    dashletsWrapperElement: HTMLElement,
    dashlet: TExportDashlet,
    assetId?: string,
  ): Observable<TDashboardImageElementData> {
    const scale = 1; // change that if needed to make sure that pdf isn't pixelated
    const canvasWrapper = this.createCanvasWrapper();
    const imageWrapper = this.createImageWrapper(scale);
    const image = createElement<HTMLImageElement>('img');
    const canvas = createElement<HTMLCanvasElement>('canvas');

    image.id = 'image';
    image.style.position = 'absolute';
    image.style.bottom = '110px';
    image.style.left = '150px';
    image.style.objectFit = 'contain';
    image.style.width = '1800px';
    image.style.height = '1083px';

    canvas.style.width = `${advancedDashletCanvasWidth}px`;
    canvas.style.height = `${advancedDashletGraphHeight}px`;
    canvas.id = 'advancedDashletCanvas';

    canvasWrapper.style.width = `${advancedDashletCanvasWidth}px`;
    canvasWrapper.style.height = `${advancedDashletGraphHeight}px`;

    imageWrapper.appendChild(image);
    canvasWrapper.appendChild(canvas);

    dashletsWrapperElement.appendChild(canvasWrapper);

    const dashletGraph: TDashletGraphSettings = {
      dashlet,
      canvas,
      imageWrapper,
      image,
      scale,
    };

    return this.createGraph(dashletGraph, assetId);
  }

  private createCanvasWrapper(): HTMLElement {
    const imageScale = 1;

    const canvasWrapper: HTMLElement = createElement('div');
    canvasWrapper.style.backgroundColor = 'white';
    canvasWrapper.style.marginLeft = `${100 * imageScale}px`;
    canvasWrapper.style.width = `${advancedDashletImageWidth * imageScale}px`;
    canvasWrapper.style.height = `${advancedDashletGraphHeight * imageScale}px`;
    canvasWrapper.style.borderRadius = `${advancedDashletBorderRadius * imageScale}px`;
    canvasWrapper.id = 'canvasWrapper';

    return canvasWrapper;
  }

  private createImageWrapper(scale: number): HTMLElement {
    const imageWrapper = createElement<HTMLElement>('div');
    imageWrapper.style.width = `${advancedDashletImageWidth * scale}px`;
    imageWrapper.style.height = `${advancedDashletHeight * scale}px`;
    imageWrapper.style.backgroundColor = 'white';
    imageWrapper.style.position = 'relative';
    imageWrapper.style.borderRadius = `${advancedDashletBorderRadius * scale}px`;
    imageWrapper.id = 'imageWrapper';

    return imageWrapper;
  }

  private createGraph(
    { dashlet, canvas, imageWrapper, image, scale }: TDashletGraphSettings,
    assetId: string,
  ): Observable<TDashboardImageElementData> {
    return this.dashletService.getExportDashletGraph(dashlet, assetId).pipe(
      switchMap((graph) =>
        this.getGraphDataResolver(dashlet, graph, canvas, assetId).pipe(
          switchMap((chartSettings) => {
            const workspaceName = dashlet.selectedRange.workspaceId
              ? this.workspaceService.findWorkspace(dashlet.selectedRange.workspaceId)?.siteName
              : dashlet.workspaceName;
            const accountName = dashlet.selectedRange.workspaceId
              ? this.workspaceService.findWorkspace(dashlet.selectedRange.workspaceId)?.accountName
              : dashlet.accountName;

            return this.completeChart(chartSettings, {
              imageWrapper,
              imageElement: image,
              chartType: dashlet.name,
              customName: dashlet.customName,
              accountName,
              workspaceName,
              scale,
            });
          }),
        ),
      ),
    );
  }

  private getGraphDataResolver(
    dashlet: TExportDashlet,
    graph: TDashletGraph,
    canvas: HTMLCanvasElement,
    assetId: string,
  ): Observable<TChartSettings> {
    switch (dashlet.name) {
      case EDashletType.CURRENT_PRIORITY:
      case EDashletType.CURRENT_STATUS:
        return this.dashboardReportService.fetchCurrentTypeCount(dashlet, graph, canvas);

      case EDashletType.OVER_TIME_PRIORITY:
      case EDashletType.OVER_TIME_STATUS:
        return this.dashboardReportService.fetchExportReports(dashlet, graph, canvas);
      case EDashletType.COST_COMPARISON:
        return this.createCostComparisonGraphService.fetchExportCostComparisonGraph(
          dashlet,
          graph,
          canvas,
          assetId,
        );
      case EDashletType.CURRENT_STACKED_STATUS:
        return this.createStackedStatusGraphService.fetchExportStackedStatusGraph(
          dashlet,
          graph,
          canvas,
        );
      case EDashletType.VALUE_BREAKDOWN:
        return this.createValueBreakdownGraphService.fetchExportValueBreakdownGraph(
          dashlet,
          graph,
          canvas,
          assetId,
        );
      default: {
        const message = `Unable to export "${dashlet.name}" dashlet`;
        console.error(message);

        return throwError(message);
      }
    }
  }

  private completeChart(
    { chart, totalPoints }: TChartSettings,
    {
      imageWrapper,
      imageElement,
      chartType,
      accountName,
      workspaceName,
      scale,
      customName,
    }: {
      imageWrapper: HTMLElement;
      imageElement: HTMLImageElement;
      chartType: EDashletType;
      accountName: string;
      workspaceName: string;
      scale: number;
      customName: string;
    },
  ): Promise<TDashboardImageElementData> {
    return new Promise((resolve) => {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          const src = chart.toBase64Image();

          const totalPointsElement =
            chartType === EDashletType.VALUE_BREAKDOWN
              ? createElement('div')
              : createTotalPointsElement(totalPoints, scale, chartType);
          const headerElement = createDashletHeaderElement({
            accountName,
            workspaceName,
            chartType,
            scale,
            customName,
          });

          imageWrapper.appendChild(totalPointsElement);
          imageWrapper.appendChild(headerElement);

          imageElement.src = src;

          generateImage({
            element: imageWrapper,
            width: advancedDashletImageWidth,
            height: advancedDashletHeight,
            wrapperWidth: advancedDashletImageWidth,
            wrapperHeight: advancedDashletHeight,
            borderRadius: advancedDashletBorderRadius,
            chartType,
            scale,
          }).subscribe((response) => {
            resolve(response);
          });
        }, 1000);
        // TODO disable animation for exported dashlets to use requestAnimationFrame() here instead of waiting 1000ms
      });
    });
  }
}
